import { useEffect, useState } from "react";
import { axiosApiInstance } from "../../../axios";
import { Disclosure } from "@headlessui/react";
import { Link, useSearchParams } from "react-router-dom";
import DatePickerInput from "../../../components/controls/DatePickerInput";
import { fromDateOnlyString, toDateOnlyString } from "../../../shared/dateOnly";
import { makeGetApiCall } from "../../../shared/makeApiCall";
import type { PaginatedResponse } from "../../../types/api";

interface Visit {
  photos?: Array<{ id: number; filename: string }>;
  id: number;
  visit_date: string;
  status: string;
  checked_in_at: string | null;
  completed_at: string | null;
  visit_notes: string | null;
  notes: string | null;
  happiness: number | null;
  route: { id: number; name: string } | null;
  visited_by: { first_name: string; last_name: string } | null;
}
const timestamp = (value: string | null) =>
  value ? new Date(value).toLocaleString() : "Not recorded";
const happinessLabels = ["Very unhappy", "Unhappy", "Neutral", "Happy", "Very happy"];

export default function CustomerVisits({
  customerId,
  logout,
}: {
  customerId: string;
  logout: () => void;
}) {
  const [params, setParams] = useSearchParams();
  const date = params.get("date") ?? "";
  const [page, setPage] = useState(1);
  const [data, setData] = useState<PaginatedResponse<Visit> | null>(null);
  const [error, setError] = useState("");
  const [retry, setRetry] = useState(0);
  useEffect(() => {
    let active = true;
    setData(null);
    setError("");
    const query = new URLSearchParams({ page: String(page) });
    if (date) query.set("date", date);
    void makeGetApiCall<PaginatedResponse<Visit>>(`customers/${customerId}/visits?${query}`).then(
      (result) => {
        if (!active) return;
        if (result.success) setData(result.data);
        else {
          if (result.errorStatus === 401) logout();
          setError(result.data.message || "Could not load visits.");
        }
      }
    );
    return () => {
      active = false;
    };
  }, [customerId, date, page, retry, logout]);
  const filterDate = (value: Date | null) => {
    setPage(1);
    setParams((current) => {
      const next = new URLSearchParams(current);
      const date = toDateOnlyString(value);
      if (date) next.set("date", date);
      else next.delete("date");
      next.delete("visit");
      return next;
    });
  };
  return (
    <section className="space-y-4" aria-label="Customer visits">
      <div className="rounded-xl border border-gray-200 bg-white p-5">
        <h2 className="font-semibold text-gray-900">Visit history</h2>
        <p className="mt-1 text-sm text-gray-500">Browse past visits or choose a specific date.</p>
        <div className="mt-4 flex flex-wrap items-end gap-3">
          <DatePickerInput
            name="visit-date-filter"
            inputId="visit-date-filter"
            label="Visit date"
            labelOn
            placeholder="All past visits"
            parentClass="w-full sm:w-72"
            className="date-picker !inline-flex w-full [&>input]:min-w-0 [&>input]:flex-1 [&>button]:shrink-0"
            value={fromDateOnlyString(date)}
            onChange={({ target }) => filterDate(target.value)}
            showIcon
            showButtonBar
            readOnlyInput
          />
          {date && (
            <button
              type="button"
              onClick={() => filterDate(null)}
              className="rounded-md border border-gray-300 px-3 py-2 text-sm hover:bg-gray-50"
            >
              Show all dates
            </button>
          )}
        </div>
      </div>
      {error ? (
        <p role="alert" className="rounded-lg bg-red-50 p-4 text-sm text-red-700">
          {error}{" "}
          <button
            type="button"
            className="underline"
            onClick={() => setRetry((value) => value + 1)}
          >
            Retry visits
          </button>
        </p>
      ) : !data ? (
        <p role="status" className="p-5 text-sm text-gray-500">
          Loading visits…
        </p>
      ) : (
        <>
          {!data.data.length && (
            <p className="rounded-xl border border-gray-200 bg-white p-8 text-center text-sm text-gray-500">
              {date ? "No visits on this date." : "No visits recorded for this customer."}
            </p>
          )}
          {data.data.map((visit) => (
            <Disclosure
              key={`${page}-${date}-${visit.id}`}
              defaultOpen={String(visit.id) === params.get("visit")}
              as="div"
              className="overflow-hidden rounded-xl border border-gray-200 bg-white"
            >
              {({ open }) => (
                <>
                  <Disclosure.Button className="flex w-full flex-wrap items-center justify-between gap-3 px-5 py-4 text-left focus-visible:outline focus-visible:outline-2 focus-visible:outline-main-purple">
                    <span className="font-semibold text-gray-900">
                      {fromDateOnlyString(visit.visit_date)?.toLocaleDateString() ??
                        visit.visit_date}
                    </span>
                    <span className="flex items-center gap-4 text-sm text-gray-500">
                      <span className="capitalize">{visit.status}</span>
                      <span aria-hidden="true">{open ? "▴" : "▾"}</span>
                    </span>
                  </Disclosure.Button>
                  <Disclosure.Panel className="space-y-4 border-t border-gray-200 px-5 py-4">
                    <dl className="grid gap-4 text-sm sm:grid-cols-2">
                      <div>
                        <dt className="font-medium text-gray-900">Route</dt>
                        <dd className="mt-1 text-gray-600">
                          {visit.route ? (
                            <Link
                              className="text-main-purple hover:underline"
                              to={`/route-planner/${visit.route.id}`}
                            >
                              {visit.route.name}
                            </Link>
                          ) : (
                            "Unavailable"
                          )}
                        </dd>
                      </div>
                      <div>
                        <dt className="font-medium text-gray-900">Recorded by</dt>
                        <dd className="mt-1 text-gray-600">
                          {visit.visited_by
                            ? `${visit.visited_by.first_name} ${visit.visited_by.last_name}`
                            : "Not recorded"}
                        </dd>
                      </div>
                      <div>
                        <dt className="font-medium text-gray-900">Check-in</dt>
                        <dd className="mt-1 text-gray-600">
                          {visit.checked_in_at ? timestamp(visit.checked_in_at) : "Not checked in"}
                        </dd>
                      </div>
                      <div>
                        <dt className="font-medium text-gray-900">Completed</dt>
                        <dd className="mt-1 text-gray-600">{timestamp(visit.completed_at)}</dd>
                      </div>
                      <div>
                        <dt className="font-medium text-gray-900">Customer happiness</dt>
                        <dd className="mt-1 text-gray-600">
                          {visit.happiness
                            ? `${happinessLabels[visit.happiness - 1]} (${visit.happiness}/5)`
                            : "Not recorded"}
                        </dd>
                      </div>
                    </dl>
                    <div className="text-sm">
                      <h3 className="font-medium text-gray-900">Visit notes</h3>
                      <p className="mt-1 whitespace-pre-wrap break-words text-gray-600">
                        {visit.visit_notes || "No visit notes recorded."}
                      </p>
                    </div>
                    <div className="text-sm">
                      <h3 className="font-medium text-gray-900">Visit photos</h3>
                      {visit.photos?.length ? (
                        <div className="mt-3 grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-4">
                          {visit.photos.map((photo) => (
                            <VisitPhoto
                              key={photo.id}
                              visitId={visit.id}
                              photo={photo}
                              logout={logout}
                            />
                          ))}
                        </div>
                      ) : (
                        <p className="mt-1 text-gray-500">No photos uploaded.</p>
                      )}
                    </div>
                    {visit.notes && (
                      <div className="text-sm">
                        <h3 className="font-medium text-gray-900">Route instructions</h3>
                        <p className="mt-1 whitespace-pre-wrap break-words text-gray-600">
                          {visit.notes}
                        </p>
                      </div>
                    )}
                  </Disclosure.Panel>
                </>
              )}
            </Disclosure>
          ))}
          {data.last_page > 1 && (
            <nav
              aria-label="Visit pagination"
              className="flex flex-wrap items-center justify-between gap-3 text-sm"
            >
              <button
                type="button"
                disabled={page === 1}
                onClick={() => setPage(page - 1)}
                className="rounded-md border border-gray-300 px-3 py-2 disabled:opacity-50"
              >
                Previous visits
              </button>
              <span>
                Page {page} of {data.last_page}
              </span>
              <button
                type="button"
                disabled={page >= data.last_page}
                onClick={() => setPage(page + 1)}
                className="rounded-md border border-gray-300 px-3 py-2 disabled:opacity-50"
              >
                Next visits
              </button>
            </nav>
          )}
        </>
      )}
    </section>
  );
}

function VisitPhoto({
  visitId,
  photo,
  logout,
}: {
  visitId: number;
  photo: { id: number; filename: string };
  logout: () => void;
}) {
  const [url, setUrl] = useState("");
  const [error, setError] = useState(false);
  useEffect(() => {
    let active = true;
    let objectUrl = "";
    void axiosApiInstance
      .get<Blob>(`route-planner/visits/${visitId}/photos/${photo.id}`, {
        responseType: "blob",
        headers: { Authorization: `Bearer ${localStorage.getItem("authToken") ?? ""}` },
      })
      .then(({ data }) => {
        if (active) {
          objectUrl = URL.createObjectURL(data);
          setUrl(objectUrl);
        }
      })
      .catch((error) => {
        if (active) {
          setError(true);
          if (error.response?.status === 401) logout();
        }
      });
    return () => {
      active = false;
      if (objectUrl) URL.revokeObjectURL(objectUrl);
    };
  }, [visitId, photo.id, logout]);
  if (error)
    return (
      <p className="rounded-lg border border-gray-200 p-3 text-xs text-red-700">
        Photo could not be loaded.
      </p>
    );
  return url ? (
    <a
      href={url}
      target="_blank"
      rel="noreferrer"
      className="overflow-hidden rounded-lg border border-gray-200 focus-visible:ring-2 focus-visible:ring-main-purple"
    >
      <img src={url} alt={photo.filename} className="aspect-square w-full object-cover" />
      <span className="block truncate px-2 py-1 text-xs text-gray-500">{photo.filename}</span>
    </a>
  ) : (
    <p role="status" className="rounded-lg bg-gray-50 p-4 text-xs text-gray-500">
      Loading photo…
    </p>
  );
}
